import type {
  ICGVariantProductVariant,
  IInitializeCGUpdater,
  IInitializePVPriceUpdater,
} from '../hooks';
import type { ICampaignGroup, IInitializeCampaignGroup } from '~/modules/campaign-group';
import type { ICampaignProduct } from '~/modules/campaign-product';
import type { ReactNode } from 'react';

import {
  useInitializeCGProductVariant,
  useInitializePVPriceUpdater,
  useInitializeCGPriceUpdater,
  useCGPriceDiscardCheck,
} from '../hooks';
import { useInitializeCampaignGroup } from '~/modules/campaign-group';
import { createContext, useContext, useMemo } from 'react';
import { GSkeletonPage } from '~/presentation/common';
import { CAMPAIGN_TYPE } from '~/constants/campaign';

export interface ICGPriceContext extends IInitializeCampaignGroup, IInitializeCGUpdater {
  isShowDiscard: boolean;
  campaignGroupProducts?: ICampaignProduct[];
  cgProductVariantState: ICGVariantProductVariant;
  pvPriceUpdater: IInitializePVPriceUpdater;
}
export interface ICGPriceProvider {
  children: ReactNode;
  campaignGroupId?: ICampaignGroup['id'];
}

export const CGPriceContext = createContext<ICGPriceContext | undefined>(undefined);

export const CGPriceProvider: React.FC<ICGPriceProvider> = ({ children, campaignGroupId }) => {
  const initializeCampaignGroup = useInitializeCampaignGroup({ campaignGroupId });
  const initializeCGProductVariant = useInitializeCGProductVariant();
  const initializePVPriceUpdater = useInitializePVPriceUpdater({
    initializeCGProductVariant,
    initializeCampaignGroup,
  });

  const { isShowDiscard } = useCGPriceDiscardCheck(
    initializeCampaignGroup.campaignGroup,
    initializeCGProductVariant.updatedPrices,
  );

  const initializeCGUpdater = useInitializeCGPriceUpdater({
    initializeCampaignGroup,
    initializeCGProductVariant,
    isShowDiscard: !!isShowDiscard,
  });

  const campaignGroupProducts = useMemo(
    () =>
      (initializeCampaignGroup.campaignGroup?.campaigns?.filter(
        (item) => item.type === CAMPAIGN_TYPE.PRICE,
      ) as ICampaignProduct[]) || [],
    [initializeCampaignGroup.campaignGroup],
  );

  const contextValue = useMemo<ICGPriceContext>(
    () => ({
      ...initializeCampaignGroup,
      ...initializeCGUpdater,
      isShowDiscard,
      campaignGroupProducts,
      pvPriceUpdater: initializePVPriceUpdater,
      cgProductVariantState: initializeCGProductVariant,
    }),
    [
      initializeCampaignGroup,
      initializeCGUpdater,
      isShowDiscard,
      campaignGroupProducts,
      initializePVPriceUpdater,
      initializeCGProductVariant,
    ],
  );

  if (!initializeCampaignGroup.campaignGroup) return <GSkeletonPage />;
  return <CGPriceContext.Provider value={contextValue}>{children}</CGPriceContext.Provider>;
};

export const useCGPriceContext = () => {
  const context = useContext(CGPriceContext);
  if (!context) {
    throw new Error('useCGPriceContext must be used within a CGPriceProvider');
  }
  return context;
};
